import {
	defaultApiVersion,
	defaultEnvironment,
	type ChatbotEndpointOptions,
	type Environment,
} from "@shared/configuration/aws-configuration";
import { provideApiVersionSetting } from "@shared/composables/settings";
import { fetchAuthSession } from "aws-amplify/auth";
import { type App, inject, ref, type Ref, watch } from "vue";

export type Settings = {
	environment: Ref<Environment>;
	apiVersion: Ref<string>; //Need to be string because of feature branch
	organizationId: Ref<string | null>;
};

const environment = ref(
	(localStorage.getItem("environment-portal") as Environment) ??
		defaultEnvironment,
);

const apiVersion = ref(
	(localStorage.getItem("apiVersion-portal") as string) ?? defaultApiVersion,
);

watch(environment, newVal =>
	localStorage.setItem("environment-portal", newVal),
);
watch(apiVersion, newVal => localStorage.setItem("apiVersion-portal", newVal));

const getStorageKey = async () => {
	try {
		const session = await fetchAuthSession();
		if (!session.tokens?.idToken) return "undefined-organizationId";
		return `${session.tokens?.idToken.payload.email}-organizationId`;
	} catch {
		return "undefined-organizationId";
	}
};

const getOrganizationIdFromStorage = async () => {
	const key = await getStorageKey();
	return localStorage.getItem(key) ?? "";
};

const setOrganizationIdToStorage = async (value: string) => {
	const key = await getStorageKey();
	localStorage.setItem(key, value);
};

const organizationId = ref("");

export const initializeOrganizationId = async () => {
	organizationId.value = await getOrganizationIdFromStorage();
};

watch(organizationId, async newVal => {
	await setOrganizationIdToStorage(newVal);
});

export const useSettings = (): Settings => {
	const settings = inject<Settings>("app-settings");
	if (!settings) {
		// Fallback for cases where injection fails (e.g., CloudFront refresh)
		console.warn("Settings injection failed, using fallback values");
		return {
			environment: environment,
			apiVersion: apiVersion,
			organizationId,
		};
	}
	return settings;
};

export function getEnvironmentSetting() {
	try {
		return (environment.value ?? defaultEnvironment) as Environment;
	} catch (e) {
		console.log(e);
		return defaultEnvironment;
	}
}

export function getApiVersionSetting() {
	try {
		return apiVersion.value ?? defaultApiVersion;
	} catch (e) {
		console.log(e);
		return defaultApiVersion;
	}
}

export const provideSettings = (app: App) => {
	provideApiVersionSetting(app, apiVersion);
	app.provide("app-settings", {
		environment: environment,
		apiVersion: apiVersion,
		organizationId,
	});
	return { environment, apiVersion, organizationId };
};

export async function getGenAIEnvironmentSetting(): Promise<
	ChatbotEndpointOptions | undefined
> {
	try {
		return (await localStorage.getItem(
			"genAIEnvironment",
		)) as ChatbotEndpointOptions;
	} catch (e) {
		console.log(e);
	}
}

export async function setGenAIEnvironmentSetting(
	genAIEnvironment: ChatbotEndpointOptions,
) {
	await localStorage.setItem("genAIEnvironment", genAIEnvironment ?? "user");
}
